import { Injectable, Injector, Optional } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { map, tap, concatMap } from 'rxjs/operators';
import { UserIdentity, AuthenticationProvider, SigninModel, OrganizationDto } from '../types';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private readonly AUTH_LOCAL_STORAGE_KEY = 'auth';
    private userAuthenticated: boolean;
    private user: UserIdentity | Observable<UserIdentity>;
    private signedInSubject = new Subject<any>();
    private signedOutSubject = new Subject<any>();
    signedIn = this.signedInSubject.asObservable();
    signedOut = this.signedOutSubject.asObservable();

    constructor(
        injector: Injector,
        @Optional() private authProvider: AuthenticationProvider,
    ) {
        if (!authProvider) {
            throw new Error('No authentication provider is registerd.');
        }

        this.readAuthFlag();
    }

    getCaptcha() {
        return this.authProvider.getCaptcha();
    }

    signin(signinModel: SigninModel) {
        return this.authProvider.signIn(signinModel).pipe(
            tap(res => {
                this.setAuthFlag(true);
                this.signedInSubject.next();
            })
        );
    }

    signout() {
        return this.authProvider.signOut().pipe(
            tap(x => {
                this.setAuthFlag(false);
                this.user = null;
                this.signedOutSubject.next();
            })
        );
    }

    changeUserPassword(currentPassword: string, newPassword: string) {
        return this.authProvider.changeUserPassword(currentPassword, newPassword)
            .pipe(
                concatMap(x => {
                    return this.getUser(true) as Observable<any>;
                })
            );
    }

    isUserAuthenticated() {
        return this.userAuthenticated;
    }

    getUser(forceReload = false): UserIdentity | Observable<UserIdentity> {
        if (!this.userAuthenticated) {
            throw Error('invalid operation. user is not authenticated.');
        }

        if (forceReload) {
            this.user = null;
        }

        if (this.user) {
            return this.user;
        } else {
            this.user = this.authProvider.getUser().pipe(
                tap(x => this.user = x)
            );
            return this.user;
        }
    }

    getUserOrganizations() {
        return this.authProvider.getUserOrganizations();
    }

    setUserOrganization(org: OrganizationDto) {
        return this.authProvider.setUserOrganization(org.id)
            .pipe(
                concatMap(x => {
                    return this.getUser(true) as Observable<any>;
                })
            );
    }

    clearAuthenticationInfo() {
        this.user = null;
        this.setAuthFlag(false);
    }

    private setAuthFlag(authenticated: boolean) {
        this.userAuthenticated = authenticated;
        if (authenticated) {
            window.sessionStorage.setItem(this.AUTH_LOCAL_STORAGE_KEY, 'true');
        } else {
            window.sessionStorage.removeItem(this.AUTH_LOCAL_STORAGE_KEY);
        }
    }

    private readAuthFlag() {
        this.userAuthenticated = window.sessionStorage.getItem(this.AUTH_LOCAL_STORAGE_KEY) === 'true';
    }

    // private setAuthTokenRefreshTimer(timeout: number) {
    //     this.refreshTokenSubscription = interval(timeout * 1000).subscribe(x => {
    //         this.authProvider.refreshAuthToken();
    //     });
    // }

    // private killAuthTokenRefreshTimer() {
    //     this.refreshTokenSubscription.unsubscribe();
    // }
}
